import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Check, Plus, Trash2 } from '../../icons';
import { Button, Checkbox, FormField, Input, Table } from '../../components/shared';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import {
  listFavoriteLines, deleteFavoriteLine, listDelegates, revokeDelegate,
  saveFavoriteLine, type FavoriteLineDto,
} from '../../api/approval';
import { ApproverPicker, type PickedUser } from './ApproverPicker';
import { listDocTypes } from '../../api/approval-doc-type';
import { useIsAdminViewer } from '../../lib/menu-catalog';
import type { ApprovalDelegatesResponse } from '../../types/approval';
import './ApprovalLinesAdmin.css';

/**
 * **결재선** — 기본 결재선과 즐겨찾기를 관리한다 (2026-08-30 · 시안 맞춤 4/9 · 2026-09-04 판 P1 에서 사다리 폐기).
 *
 * ## ★★★ 사다리를 고치는 단추는 여기 없다
 *
 * 팀장은 **조직도**, 부서장·대표는 **인사 › 대표 · 부서장**에서 정한다 — **값의 주인이 정한
 * 자리**로 보낸다. 두 곳에서 고칠 수 있게 하면 어느 쪽이 진짜인지 흐려진다.
 * 여기서는 **읽고, 빈 칸을 세고, 고치러 갈 길만** 준다.
 *
 * ## ★★ 「빈 칸」을 머리에 센다
 *
 * 부서장이 비면 그 단계 **없이** 대표로 올라간다 — 결재선이 조용히 짧아지고 **아무도 모른다.**
 * 지금 가장 흔한 사고라, 화면이 먼저 센다.
 *
 * ## ★★ 지우는 단추가 처음 생긴다
 *
 * 서버에는 즐겨찾기를 지우는 길이 **이미 있는데 부르는 화면이 없어**, 한 번 만든 즐겨찾기가
 * 영영 남았다. 지울 수 있는지는 **서버가 판정한다**(`canDelete`) — 화면이 규칙을 다시 쓰면
 * 서버와 갈라진다.
 *
 * ## ★ 위임은 지우지 않고 끈다
 *
 * 「그때 누가 대신 결재했나」가 문서에 남아야 한다. 담당자는 여기서 **회사 전체**를 보고,
 * 개인은 자기 화면에서 자기 것을 건다.
 */

/**
 * 앞 결재자는 최대 셋 (정본 B11-3) — 기안 화면·서버와 같은 값이다.
 * ⚠ 이름 `MAX_REVIEWERS` 는 «옛 이름(검토자)» 자취다 — 2026-09-24 에 라벨만 바뀌었고 수는 그대로다.
 */
const MAX_REVIEWERS = 3;

export function ApprovalLinesAdmin(): JSX.Element {
  const [favorites, setFavorites] = useState<FavoriteLineDto[] | null>(null);
  const [delegates, setDelegates] = useState<ApprovalDelegatesResponse | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const isAdmin = useIsAdminViewer();
  /**
   * ★★ **만드는 자리가 없었다** (2026-08-31 · 시안 F3). 즐겨찾는 결재선은 **기안 창 안에서만**
   *    만들 수 있어서, 회사가 공용 결재선을 미리 깔아 두려면 문서를 하나 쓰기 시작해야 했다.
   *    시안이 이 카드에 「만들기」를 그린 이유가 그것이다.
   */
  const [making, setMaking] = useState(false);
  const [newName, setNewName] = useState('');
  /**
   * ★★ 기안 화면과 **같은 어법** — 결재자 여럿 + 최종결재자 하나가 저장될 때 한 배열이 된다(판 P1).
   *    여기서 한 칸으로 뭉뚱그려 받으면 회사가 깐 줄과 기안 화면이 다른 말을 하게 된다.
   */
  const [newReviewers, setNewReviewers] = useState<PickedUser[]>([]);
  const [newApprover, setNewApprover] = useState<PickedUser[]>([]);
  const [newReferrers, setNewReferrers] = useState<PickedUser[]>([]);
  const [newShared, setNewShared] = useState(false);
  /** 이 줄을 어느 양식의 기본으로 쓸까 — 공용일 때만 뜻이 있다. */
  const [newDefaults, setNewDefaults] = useState<string[]>([]);
  /** 고를 수 있는 양식 — 못 읽어도 결재선 저장은 된다(그 사실을 화면이 말한다). */
  const [docTypes, setDocTypes] = useState<Array<{ code: string; name: string }> | null>(null);

  const load = useCallback(async () => {
    setError(null);
    // ★ 셋을 따로 읽는다 — 하나가 막혀도 나머지는 선다.
    //   조직(`getOrgLeaders`)은 더 읽지 않는다 — 사다리를 안 쓰므로 이 화면과 무관해졌다(판 P1).
    void listFavoriteLines().then(setFavorites).catch(() => setFavorites(null));
    void listDelegates('company').then(setDelegates).catch(() => setDelegates(null));
    void listDocTypes()
      .then((ts) => setDocTypes(ts.filter((t) => t.isActive).map((t) => ({ code: t.code, name: t.name }))))
      .catch(() => setDocTypes(null));
  }, []);

  useEffect(() => { void load(); }, [load]);

  /** 그 양식의 기본을 **지금 들고 있는 줄**의 이름 — 「옮겨 온다」를 미리 말하려고 본다. */
  const ownerOfDefault = (code: string): string | null =>
    (favorites ?? []).find((f) => (f.defaultForTypes ?? []).includes(code))?.name ?? null;

  const closeMake = () => {
    setMaking(false); setNewName('');
    setNewReviewers([]); setNewApprover([]); setNewReferrers([]);
    setNewShared(false); setNewDefaults([]);
  };

  const createFavorite = async () => {
    const name = newName.trim();
    // 앞 결재자는 없어도 되지만 **최종결재자는 비울 수 없다** — 서버도 같은 규칙으로 막는다.
    if (!name || newApprover.length === 0) return;
    setBusy('new'); setError(null); setDone(null);
    try {
      const res = await saveFavoriteLine({
        name,
        // ★ 마지막이 「최종결재자」다 — 앞 결재자를 앞에, 최종결재자를 뒤에 붙인다.
        approverIds: [...newReviewers, ...newApprover].map((u) => u.userId),
        ...(newReferrers.length > 0 ? { referrerIds: newReferrers.map((u) => u.userId) } : {}),
        // ★ **켤 때만 싣는다** — 옛 서버는 이 칸을 모르고 `additionalProperties:false` 라 400 이다.
        ...(newShared ? { shared: true } : {}),
        ...(newShared && newDefaults.length > 0 ? { defaultForTypes: newDefaults } : {}),
      });
      closeMake();
      // ★ 「옮겨 왔다」를 밝힌다 — 한 양식에 기본은 하나라 남의 줄에서 빠진다(서버가 한다).
      const moved = res.movedFrom ?? [];
      setDone(
        moved.length > 0
          ? `「${name}」 을 저장했습니다 — 기본을 「${moved.join('」·「')}」 에서 옮겨 왔습니다.`
          : `「${name}」 을 저장했습니다.`,
      );
      setFavorites(await listFavoriteLines());
    } catch {
      setError('저장하지 못했습니다 — 공용 결재선은 회사 관리자만 만듭니다.');
    } finally { setBusy(null); }
  };

  const removeFavorite = async (f: FavoriteLineDto) => {
    if (!window.confirm(`「${f.name}」 을 지웁니다. 계속할까요?`)) return;
    setBusy(f.id); setError(null); setDone(null);
    try {
      await deleteFavoriteLine(f.id);
      setDone(`「${f.name}」 을 지웠습니다.`);
      setFavorites(await listFavoriteLines());
    } catch {
      setError('지우지 못했습니다 — 공용 결재선은 회사 관리자만 지웁니다.');
    } finally { setBusy(null); }
  };

  const stopDelegate = async (id: string, who: string) => {
    if (!window.confirm(`${who} 님의 위임을 끕니다. 기록은 남습니다. 계속할까요?`)) return;
    setBusy(id); setError(null); setDone(null);
    try {
      await revokeDelegate(id);
      setDone('위임을 껐습니다 — 기록은 남습니다.');
      setDelegates(await listDelegates('company'));
    } catch {
      // ★ 서버는 **맡긴 사람만** 끌 수 있게 한다 — 담당자라도 남의 위임은 못 끈다.
      setError('끄지 못했습니다 — 위임은 맡긴 본인만 끌 수 있습니다.');
    } finally { setBusy(null); }
  };

  return (
    <div className="apl">
      {/* 사다리를 안 쓰므로 「빈 칸 N」 배지는 결재와 무관해졌다 — 세지 않는다(판 P1). */}
      <PageHeader
        breadcrumb={crumbsFor('/approval/admin/lines')}
        scope={scopeFor('/approval/admin/lines')}
        title="결재선"
        description="회사 공용 결재선을 만들고, 어느 양식의 기본으로 쓸지 정합니다"
      />

      {error && <p className="apl__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {done && <p className="apl__ok" role="status"><Check size={14} aria-hidden />{done}</p>}

      {/*
        ⚠⚠ **2026-09-04 — 사다리 세 칸을 걷어냈다** (판 P1).

           사용자 결정으로 결재선은 조직 사다리가 아니라 **회사가 깔아 둔 기본 결재선**에서
           온다. 「1단계 팀장 → 2단계 부서장 → 3단계 대표」를 그리면 이제 **거짓말**이고,
           「빈 칸을 세는 경고」는 있지도 않은 문제를 만든다.
           근거: `docs/design/adr-20260904-approval-line-user-composed.md`
      */}
      <section className="apl__card" aria-label="결재선이 정해지는 법">
        <h2 className="apl__cardt">
          결재선은 이렇게 정해집니다 <span className="apl__dim">사람이 만든다</span>
        </h2>
        <ol className="apl__how">
          <li>
            <b>회사가 기본 결재선을 깝니다</b> — 아래에서 공용 결재선을 만들고, 어느 양식의
            기본으로 쓸지 고릅니다.
          </li>
          <li>
            <b>기안할 때 자동으로 배치됩니다</b> — 기안자는 그 자리에서 빼고 더하고 순서를 바꿉니다.
          </li>
          <li>
            <b>기본 결재선이 없으면 결재자 · 최종결재자 칸이 빈 채로</b> 섭니다 — 기안을 막지 않고,
            결재자를 고르지 않은 채 상신할 때만 막습니다.
          </li>
        </ol>
        <p className="apl__dim">
          결재자는 최대 3명 · 최종결재자는 반드시 1명입니다. 참고자는 결재하지 않고 문서를 볼 수만 있습니다.
        </p>
      </section>

      <div className="apl__two">
        {/* ── 즐겨찾는 결재선 ── */}
        <section className="apl__card" aria-label="즐겨찾는 결재선">
          <h2 className="apl__cardt">
            즐겨찾는 결재선 <span className="apl__dim">회사 공용 · 개인</span>
            <span className="apl__sp" />
            <Button
              variant="secondary" size="sm"
              aria-expanded={making}
              onClick={() => (making ? closeMake() : setMaking(true))}
              iconLeft={<Plus size={13} aria-hidden />}
            >
              {making ? '접기' : '만들기'}
            </Button>
          </h2>

          {making && (
            <div className="apl__mkform">
              <FormField className="apl__field" label="이름">
                <Input
                  fullWidth type="text" value={newName} maxLength={60} autoFocus
                  placeholder="예: 팀장 → 대표"
                  onChange={(e) => setNewName(e.target.value)}
                />
              </FormField>
              {/* 기안 화면과 **같은 어법**으로 담는다 — 저장은 한 배열이고 마지막이 「최종결재자」다. */}
              <ApproverPicker
                label="결재자" hint={`거쳐 가는 사람 · 순서대로 · 없어도 됩니다 (최대 ${MAX_REVIEWERS}명)`}
                value={newReviewers} onChange={setNewReviewers} max={MAX_REVIEWERS}
                exclude={[...newApprover.map((u) => u.userId), ...newReferrers.map((u) => u.userId)]}
              />
              <ApproverPicker
                label="최종결재자" hint="마지막 한 사람 · 비울 수 없습니다"
                value={newApprover} onChange={(v) => setNewApprover(v.slice(-1))} max={1}
                exclude={[...newReviewers.map((u) => u.userId), ...newReferrers.map((u) => u.userId)]}
              />
              <ApproverPicker
                label="참고자" hint="결재하지 않습니다 — 문서를 볼 수만 있습니다"
                value={newReferrers} onChange={setNewReferrers}
                exclude={[...newReviewers, ...newApprover].map((u) => u.userId)}
              />
              {/* ★ 공용은 **회사 관리자만** 켤 수 있고 서버가 다시 본다 — 못 켜는 사람에게는 안 세운다. */}
              {isAdmin && (
                <Checkbox
                  className="apl__check"
                  label="회사 공용으로 — 모두가 함께 씁니다"
                  checked={newShared}
                  onChange={(e) => setNewShared(e.target.checked)}
                />
              )}

              {/*
                ★★★ **이것이 판 P1 의 입구다** — 여기서 고르지 않으면 어떤 기안도 자동으로
                     채워지지 않는다. 「표가 있다 ≠ 쓸 수 있다」를 되풀이하지 않으려고 만든 칸.
                ★ 공용일 때만 세운다 — 개인 줄은 남의 기안을 채울 수 없다(서버도 버린다).
              */}
              {isAdmin && newShared && (
                <div className="apl__field">
                  <span>이 양식의 기본으로 <small className="apl__dim">기안하면 처음부터 들어옵니다</small></span>
                  {docTypes === null ? (
                    <p className="apl__dim">양식 목록을 불러오지 못했습니다 — 결재선은 그대로 저장됩니다.</p>
                  ) : docTypes.length === 0 ? (
                    <p className="apl__dim">쓸 수 있는 양식이 없습니다.</p>
                  ) : (
                    <ul className="apl__typepick">
                      {docTypes.map((t) => (
                        <li key={t.code}>
                          <Checkbox
                            className="apl__check"
                            checked={newDefaults.includes(t.code)}
                            onChange={(e) => setNewDefaults((prev) => (
                              e.target.checked
                                ? [...prev, t.code]
                                : prev.filter((c) => c !== t.code)
                            ))}
                            label={(
                              <>
                                {t.name}
                                {/* ★ 이미 다른 줄이 그 양식의 기본이면 «옮겨 온다»고 미리 말한다. */}
                                {ownerOfDefault(t.code) && (
                                  <small className="apl__dim"> · 지금은 「{ownerOfDefault(t.code)}」</small>
                                )}
                              </>
                            )}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <div className="apl__mkacts">
                <Button variant="secondary" size="sm" onClick={closeMake}>취소</Button>
                <Button
                  size="sm"
                  disabled={busy === 'new' || newName.trim() === '' || newApprover.length === 0}
                  onClick={() => { void createFavorite(); }}
                >{busy === 'new' ? '저장 중…' : '저장'}</Button>
              </div>
            </div>
          )}
          {favorites === null ? (
            <p className="apl__dim">불러오지 못했습니다.</p>
          ) : favorites.length === 0 ? (
            <p className="apl__dim">저장된 결재선이 없습니다 — 문서를 쓸 때 결재선을 짜고 이름을 붙이면 여기 남습니다.</p>
          ) : (
            <Table
              caption="즐겨찾는 결재선"
              rows={favorites}
              rowKey={(f) => f.id}
              columns={[
                {
                  key: 'name', header: '이름', rowHeader: true,
                  render: (f) => (
                    <>
                      {f.name}
                      {f.shared && <span className="apl__bd is-shared">공용</span>}
                      {f.shared && f.ownerName && <span className="apl__dim"> · {f.ownerName}</span>}
                      {/*
                        ★★ **어느 양식의 기본인지 목록에서 바로 보인다** (판 P1).
                           안 보이면 「왜 이 결재선이 자동으로 들어왔지」를 아무도 못 되짚는다 —
                           동작을 가르는 값은 화면에 있어야 한다.
                      */}
                      {(f.defaultForTypes ?? []).length > 0 && (
                        <span className="apl__bd is-default">
                          기본 · {(f.defaultForTypes ?? [])
                            .map((c) => docTypes?.find((t) => t.code === c)?.name ?? c)
                            .join(' · ')}
                        </span>
                      )}
                    </>
                  ),
                },
                {
                  key: 'line', header: '줄',
                  render: (f) => (
                    <span className="apl__dim">
                      {/* 마지막 하나가 「최종결재자」다 — 그 앞은 결재자. */}
                      결재자 {Math.max(0, f.approvers.length - 1)} · 최종결재자 {f.approvers.length > 0 ? 1 : 0} · 참고자 {f.referrers.length}
                      {/* ★ 회사를 떠난 사람이 섞였으면 **불러오기 전에** 알린다. */}
                      {f.missing > 0 && <span className="apl__gap"> 퇴사 {f.missing}</span>}
                    </span>
                  ),
                },
                {
                  key: 'del', header: '지우기', align: 'center', width: '84px',
                  render: (f) => (
                    /*
                      ★★ 지우는 단추가 **처음 생긴다.** 서버에 길이 있는데 부르는 화면이 없어
                         한 번 만든 즐겨찾기가 영영 남았다.
                      ★ 지울 수 있는지는 **서버가 판정한다**(canDelete) — 옛 서버는 안 준다 → 늘 보인다.
                    */
                    f.canDelete !== false ? (
                      <Button
                        variant="quiet-danger" size="sm" iconOnly
                        aria-label={`${f.name} 지우기`}
                        disabled={busy === f.id}
                        onClick={() => { void removeFavorite(f); }}
                        iconLeft={<Trash2 size={13} aria-hidden />}
                      />
                    ) : null
                  ),
                },
              ]}
            />
          )}
        </section>

        {/* ── 위임 ── */}
        <section className="apl__card" aria-label="위임">
          <h2 className="apl__cardt">
            위임 <span className="apl__dim">회사 전체 · 기간</span>
            <span className="apl__sp" />
            {/*
              ★ 「새 위임」은 **내 위임 화면**으로 보낸다 — 서버는 위임을 **맡기는 본인만** 걸 수
                있게 한다(남을 대신 걸어 주는 길이 없다). 여기서 폼을 그리면 눌러도 안 되는
                단추가 되므로, **할 수 있는 자리로** 보낸다.
            */}
            {/* ★ 링크인데 «단추로 보여야» 하는 것 — 정본 Button 의 `href` 가 그 자리다. */}
            <Button
              variant="secondary" size="sm" href="/approval/me/delegates"
              iconLeft={<Plus size={13} aria-hidden />}
            >
              새 위임
            </Button>
          </h2>
          {delegates === null ? (
            <p className="apl__dim">불러오지 못했습니다.</p>
          ) : delegates.outgoing.length === 0 ? (
            <p className="apl__dim">걸린 위임이 없습니다.</p>
          ) : (
            <Table
              caption="걸린 위임"
              rows={delegates.outgoing}
              rowKey={(d) => d.id}
              columns={[
                { key: 'principal', header: '맡긴 사람', rowHeader: true,
                  render: (d) => d.principalName ?? '나' },
                { key: 'delegate', header: '대신할 사람',
                  render: (d) => d.delegateName ?? d.delegateId },
                { key: 'period', header: '기간', numeric: true,
                  render: (d) => <span className="apl__dim">{d.startDate.slice(5)} ~ {d.endDate.slice(5)}</span> },
                { key: 'state', header: '상태',
                  render: (d) => (
                    <span className={
                      !d.isActive ? 'apl__bd' : d.running ? 'apl__bd is-run' : d.upcoming ? 'apl__bd is-wait' : 'apl__bd'
                    }>
                      {!d.isActive ? '해지' : d.running ? '진행' : d.upcoming ? '예정' : '지남'}
                    </span>
                  ) },
                { key: 'off', header: '끄기', align: 'center', width: '84px',
                  render: (d) => (d.isActive ? (
                    <Button
                      variant="quiet-danger" size="sm"
                      aria-label={`${d.delegateName ?? '위임'} 끄기`}
                      disabled={busy === d.id}
                      onClick={() => { void stopDelegate(d.id, d.principalName ?? '내'); }}
                    >끄기</Button>
                  ) : null) },
              ]}
            />
          )}
          <p className="apl__note">
            <b>위임은 지우지 않고 끕니다</b> — 「그때 누가 대신 결재했나」가 문서에 남아야 합니다.
            {/* ★ 회사 전체를 못 본 경우를 구별해 적는다 — 「없다」와 「내 것만 왔다」는 다른 말이다. */}
            {delegates && delegates.scope !== 'company' && (
              <> 지금은 <b>내가 맡긴 것만</b> 보입니다 — 회사 전체는 <b>회사 관리자</b>가 봅니다.</>
            )}
            {' '}끄는 것은 <b>맡긴 본인만</b> 할 수 있습니다.
          </p>
        </section>
      </div>
    </div>
  );
}

export default ApprovalLinesAdmin;
